import { JOB_QUEUE_CONFIG } from '@/shared/config/job-queue.config';

// ===== MONOTONIC CLOCK ANCHORING =====
// Uses performance.now() (monotonic) instead of Date.now() (wall clock)
// to avoid device-server clock skew issues.

/**
 * Anchor point for monotonic progress calculation.
 * Created when a job transitions to processing (from server event or snapshot).
 */
export interface ProgressAnchor {
  /** performance.now() when anchor was created */
  anchorUptime: number;
  /** Server progress percentage (0-95) at anchor time */
  anchorProgress: number;
  /** Total predicted duration in seconds */
  durationPredicted: number;
}

/** Module-level map of job ID → monotonic progress anchor */
const progressAnchors = new Map<string, ProgressAnchor>();

/** Get progress anchor for a job */
export function getProgressAnchor(jobId: string): ProgressAnchor | undefined {
  return progressAnchors.get(jobId);
}

/** Create or update a progress anchor for a job */
export function setProgressAnchor(jobId: string, anchor: ProgressAnchor): void {
  progressAnchors.set(jobId, anchor);
}

/** Remove progress anchor for a job (on terminal state) */
export function removeProgressAnchor(jobId: string): void {
  progressAnchors.delete(jobId);
}

/** Clear all progress anchors (on logout/reset) */
export function clearAllProgressAnchors(): void {
  progressAnchors.clear();
}

/**
 * Calculate progress using monotonic anchor (immune to clock skew).
 * @returns Progress percentage (0-95), or null if no anchor
 */
function calculateFromAnchor(jobId: string): number | null {
  const anchor = progressAnchors.get(jobId);
  if (!anchor || anchor.durationPredicted <= 0) return null;

  const elapsedSinceAnchorMs = performance.now() - anchor.anchorUptime;
  const elapsedSinceAnchorSec = elapsedSinceAnchorMs / 1000;
  const extrapolated =
    anchor.anchorProgress +
    (elapsedSinceAnchorSec / anchor.durationPredicted) * 100;

  return Math.min(
    JOB_QUEUE_CONFIG.PROGRESS.MAX_PREDICTED_PROGRESS,
    Math.floor(extrapolated)
  );
}

/**
 * Calculate progress percentage based on elapsed time and predicted duration
 * Caps at MAX_PREDICTED_PROGRESS (95%) to show completion only when actually done
 *
 * @param elapsed - Time elapsed (any unit, must match durationPredicted)
 * @param durationPredicted - Predicted total duration (same unit as elapsed)
 * @returns Progress percentage (0-95)
 */
export const calculateJobProgress = (
  elapsed: number,
  durationPredicted: number
): number => {
  if (durationPredicted <= 0) return 0;
  return Math.min(
    JOB_QUEUE_CONFIG.PROGRESS.MAX_PREDICTED_PROGRESS,
    (elapsed / durationPredicted) * 100
  );
};

/**
 * Calculate progress for a job that started at a given time.
 * If jobId is provided, uses monotonic anchor (immune to clock skew).
 * Falls back to wall clock comparison if no anchor available.
 *
 * @param startedAt - ISO date string when job started
 * @param durationPredicted - Predicted duration in seconds
 * @param jobId - Optional job ID for monotonic anchor lookup
 * @returns Progress percentage (0-95), rounded
 */
export const calculateJobProgressFromStart = (
  startedAt?: string,
  durationPredicted?: number,
  jobId?: string
): number => {
  // Try monotonic anchor first (immune to clock skew)
  if (jobId) {
    const anchorProgress = calculateFromAnchor(jobId);
    if (anchorProgress !== null) return anchorProgress;
  }

  // Fallback to wall clock
  if (!startedAt || !durationPredicted) return 0;
  const started = new Date(startedAt).getTime();
  const now = Date.now();
  const elapsedSeconds = (now - started) / 1000;
  return Math.round(calculateJobProgress(elapsedSeconds, durationPredicted));
};
